import AsyncSelect, { Option } from "@/components/AsyncSelect";
import { useTranslation } from "react-i18next";
import { ComponentProps, forwardRef, useCallback } from "react";
import { ControllerRenderProps } from "react-hook-form";
import { EntityListLink } from "@/components/EntityListLink";
import { Module } from "@/constants";
import { fetchUserList } from "@/services/users";
import { FilterOperator, FilterType } from "@/lib/filters";
import { ReadUser, SystemRoleCodesType } from "@/types/user";
import { useCreateDataTableOptions } from "@/components/data-table/hooks/useCreateDataTableOptions";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import appConfiguration from "@/components/cfg/AppCfg";

interface UserSelectProps extends ControllerRenderProps {
  className?: string;
  inDialog?: boolean;
  systemRoleCodes?: SystemRoleCodesType | SystemRoleCodesType[];
  showEntityLink?: boolean;
  showRole?: boolean;
  defaultItem?: Partial<Option>;
}

export default forwardRef<HTMLButtonElement, UserSelectProps>(
  function UserSelect(
    {
      value,
      inDialog,
      systemRoleCodes = [],
      showEntityLink = false,
      showRole = false,
      defaultItem,
      ...props
    },
    ref,
  ) {
    const { t } = useTranslation();

    const systemRoleCodesString = Array.isArray(systemRoleCodes)
      ? systemRoleCodes.join(` ${FilterOperator.OR} `)
      : systemRoleCodes;

    const name = "usersSelect";
    const options = useCreateDataTableOptions(name);
    const queryKey = useDynamicQueryParams<ReadUser>(name, options, {
      systemRoleCodes: {
        value: systemRoleCodesString,
        filter: FilterType.EqualsString,
      },
    });

    const renderName = useCallback(
      (item: ReadUser & Option) => {
        return (
          <span className="flex flex-col items-start gap-0">
            <span>{item.full_name ?? item.name}</span>
            {showRole && item.system_role_code && (
              <span className="text-gray-400 text-xs">
                ({t(`userRole.${item.system_role_code}`)})
              </span>
            )}
          </span>
        );
      },
      [showRole, t],
    );

    const onFilter: ComponentProps<typeof AsyncSelect>["onChange"] =
      useCallback(
        (filterValue) => {
          options.setFilterValue("full_name", {
            value: filterValue,
            filter: FilterType.IncludesString,
          });
        },
        [options],
      );

    return (
      <span className="flex items-center gap-1">
        <AsyncSelect
          queryKey={queryKey}
          queryFn={() =>
            fetchUserList({
              filters: [
                ...options.filters,
                {
                  id: "system_role_code",
                  value: {
                    value: systemRoleCodesString,
                    filter: FilterType.EqualsString,
                  },
                },
              ],
              limit: appConfiguration.selectComponentsPaginationLimit,
            })
          }
          placeholder={t("userSelect.placeholder")}
          loadingLabel={t("userSelect.loadingUsers")}
          {...props}
          value={value ? value.toString() : undefined}
          ref={ref}
          enableFilter
          onFilter={onFilter}
          inDialog={inDialog}
          renderName={renderName}
          defaultItem={defaultItem}
        />
        {showEntityLink && (
          <EntityListLink path={Module.Users} label={t("userSelect.link")} />
        )}
      </span>
    );
  },
);
